import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { 
  Plus, 
  Users, 
  Search, 
  MoreVertical, 
  Pencil, 
  Trash2,
  Mail,
  Phone,
  Shield,
  UserCircle
} from 'lucide-react';

const MitarbeiterView = () => {
  const [employees, setEmployees] = useState([
    {
      id: '1',
      name: 'Max Mustermann',
      email: 'max@steuerstrom.de',
      telefon: '0170 1234567',
      rolle: 'admin',
      status: 'aktiv'
    },
    {
      id: '2',
      name: 'Erika Musterfrau',
      email: 'erika@steuerstrom.de',
      telefon: '0171 9876543',
      rolle: 'techniker',
      status: 'aktiv'
    }
  ]);

  const [isNewEmployeeOpen, setIsNewEmployeeOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newEmployee, setNewEmployee] = useState({
    name: '', email: '', telefon: '', rolle: 'techniker'
  });

  const roles = [
    { value: 'admin', label: 'Administrator', color: 'bg-red-100 text-red-700' },
    { value: 'planer', label: 'Planer', color: 'bg-blue-100 text-blue-700' },
    { value: 'techniker', label: 'Techniker', color: 'bg-green-100 text-green-700' },
    { value: 'readonly', label: 'Nur Lesen', color: 'bg-gray-100 text-gray-700' }
  ];

  const handleCreateEmployee = () => {
    if (newEmployee.name.trim() && newEmployee.email.trim()) {
      setEmployees([...employees, {
        id: Date.now().toString(),
        ...newEmployee,
        status: 'aktiv'
      }]);
      setNewEmployee({ name: '', email: '', telefon: '', rolle: 'techniker' });
      setIsNewEmployeeOpen(false);
    }
  };

  const handleDeleteEmployee = (id) => {
    if (window.confirm('Möchten Sie diesen Mitarbeiter wirklich entfernen?')) {
      setEmployees(employees.filter(e => e.id !== id));
    }
  };

  const getRoleInfo = (rolle) => roles.find(r => r.value === rolle) || roles[2];

  const filteredEmployees = employees.filter(e =>
    e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-800">Mitarbeiterverwaltung</h1>
            <p className="text-sm text-gray-500 mt-1">{employees.length} Mitarbeiter</p>
          </div>
          <Button onClick={() => setIsNewEmployeeOpen(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Mitarbeiter einladen
          </Button>
        </div>

        {/* Search */}
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Mitarbeiter suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Employee List */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-3">
          {filteredEmployees.map((employee) => {
            const roleInfo = getRoleInfo(employee.rolle);
            return (
              <Card key={employee.id} className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                      <UserCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800">{employee.name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {employee.email}
                        </span>
                        {employee.telefon && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {employee.telefon}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleInfo.color}`}>
                      {roleInfo.label}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Pencil className="w-4 h-4 mr-2" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Shield className="w-4 h-4 mr-2" />
                          Rolle ändern
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDeleteEmployee(employee.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Entfernen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* New Employee Dialog */}
      <Dialog open={isNewEmployeeOpen} onOpenChange={setIsNewEmployeeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mitarbeiter einladen</DialogTitle>
            <DialogDescription>
              Der Mitarbeiter erhält eine E-Mail mit einem Einladungslink.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newEmployee.name}
                onChange={(e) => setNewEmployee({...newEmployee, name: e.target.value})}
                placeholder="z.B. Max Mustermann"
              />
            </div>
            <div className="space-y-2">
              <Label>E-Mail *</Label>
              <Input
                type="email"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                placeholder="z.B. max@firma.de"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input
                value={newEmployee.telefon}
                onChange={(e) => setNewEmployee({...newEmployee, telefon: e.target.value})}
                placeholder="z.B. 0170 1234567"
              />
            </div>
            <div className="space-y-2">
              <Label>Rolle</Label>
              <Select
                value={newEmployee.rolle}
                onValueChange={(value) => setNewEmployee({...newEmployee, rolle: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsNewEmployeeOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateEmployee} className="bg-emerald-600 hover:bg-emerald-700">
              Einladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MitarbeiterView;
